import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { FolderData, FolderContextType } from '../../types/folder';

const FolderContext = createContext<FolderContextType | null>(null);

export const useFolderContext = () => {
  const context = useContext(FolderContext);
  if (!context) {
    throw new Error('useFolderContext must be used within FolderProvider');
  }
  return context;
};

interface FolderProviderProps {
  children: React.ReactNode;
  folders: FolderData[];
  externalOpenFolderIds?: Set<string>;
  onOpenFolderIdsChange?: (openFolderIds: Set<string>) => void;
}

export const FolderProvider: React.FC<FolderProviderProps> = ({ 
  children, 
  folders, 
  externalOpenFolderIds, 
  onOpenFolderIdsChange 
}) => {
  const [openFolderIds, setOpenFolderIds] = useState<Set<string>>(externalOpenFolderIds || new Set());

  // 外部のopenFolderIdsが変更された場合に同期
  useEffect(() => {
    if (externalOpenFolderIds) {
      setOpenFolderIds(externalOpenFolderIds);
    }
  }, [externalOpenFolderIds]);

  const toggleFolder = useCallback((folderId: string) => {
    setOpenFolderIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }

      // 外部に変更を通知
      if (onOpenFolderIdsChange) {
        onOpenFolderIdsChange(newSet);
      }

      return newSet;
    });
  }, [openFolderIds, onOpenFolderIdsChange]);

  const closeFolderAndChildren = useCallback((folderId: string) => {
    setOpenFolderIds(prev => {
      const newSet = new Set(prev);
      
      // 現在のフォルダを閉じる
      newSet.delete(folderId);
      
      // 子フォルダも再帰的に閉じる
      const closeChildren = (parentId: string) => {
        folders
          .filter(f => f.parentId === parentId)
          .forEach(child => {
            newSet.delete(child.id);
            closeChildren(child.id);
          });
      };
      
      closeChildren(folderId);
      return newSet;
    });
  }, [folders]);

  const openFolder = useCallback((folderId: string) => {
    setOpenFolderIds(prev => new Set(Array.from(prev).concat(folderId)));
  }, []);

  const closeFolder = useCallback((folderId: string) => {
    setOpenFolderIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(folderId);
      return newSet;
    });
  }, []);

  // Contextのレンダリング最適化
  const value = useMemo(
    () => ({
      openFolderIds,
      toggleFolderOpen: toggleFolder,
      closeFolderAndChildren,
      openFolder,
      closeFolder,
    }),
    [openFolderIds, toggleFolder, closeFolderAndChildren, openFolder, closeFolder]
  );

  return (
    <FolderContext.Provider value={value}>
      {children}
    </FolderContext.Provider>
  );
};
